import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { Scenario } from '@/api/rest/resources/types/fact';
import { NRPMode } from '@/api/rest/resources/types/scenarioForecast';
import { WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';

import { NRP_METHODOLOGY_URL } from '../constants';
import { useEditScenario } from '../hooks/useEditScenario';

export const ScenarioSelector = () => {
  const { t } = useTranslation();
  const { form, onSubmit } = useEditScenario();

  const HEADER_CLASSNAME = 'typography-overline font-semibold text-text-primary whitespace-nowrap';

  const defaultConfiguration: WidgetSlotConfiguration = {
    name: 'scenario_selector',
    type: WidgetType.a_stack,
    widgets: [
      {
        name: 'scenario_selector_form',
        type: WidgetType.o_form,
        visible: true,
        relatedData: {
          id: 'scenario-selector-form',
          key: 'scenario-selector-form',
          controlsFormArg: form,
          onSubmit,
          className: 'flex flex-col gap-8 border border-neutral-black-12 rounded-lg p-5 h-fit min-w-min',
        },
        widgets: [
          {
            name: 'scenario_selector_timeline',
            type: WidgetType.o_form_slider,
            visible: true,
            relatedData: {
              formName: 'biophysical_horizon',
              label: t('shared.scenarioAnalysis.scenarioSelector.form.fields.biophysical_horizon.label'),
              min: 1,
              max: 25,
              step: 1,
              showMinMax: true,
              styles: {
                label: HEADER_CLASSNAME,
              },
            },
          },
          {
            name: 'scenario_selector_climate_scenario_options',
            type: WidgetType.a_stack,
            visible: true,
            relatedData: {
              className: 'gap-4',
            },
            widgets: [
              {
                name: 'scenario_selector_climate_scenario_label',
                type: WidgetType.a_text,
                visible: true,
                relatedData: {
                  text: t('shared.scenarioAnalysis.scenarioSelector.form.fields.climate_scenario.label'),
                  className: HEADER_CLASSNAME,
                },
              },
              {
                name: 'scenario_selector_climate_scenario_options_radio_group',
                type: WidgetType.o_form_radio_group,
                visible: true,
                relatedData: {
                  name: 'climate_scenario',
                  className: 'gap-3',
                },
                widgets: Object.keys(Scenario).map((climateScenario) => {
                  return {
                    name: `scenario_selector_climate_scenario_option_${climateScenario}`,
                    type: WidgetType.o_form_radio_item,
                    visible: true,
                    relatedData: {
                      styles: {
                        label: 'whitespace-break-spaces',
                      },
                      key: `climate-scenario-radio-${climateScenario}`,
                      label: (
                        <Stack>
                          <span className=''>
                            {t(`climateRisk.scenarios.${climateScenario as keyof typeof Scenario}.title`)}
                          </span>
                          <span className='typography-caption text-text-secondary'>
                            {t(`climateRisk.scenarios.${climateScenario as keyof typeof Scenario}.description`)}
                          </span>
                        </Stack>
                      ),
                      value: `${climateScenario}`,
                      tooltip: t(`climateRisk.scenarios.${climateScenario as keyof typeof Scenario}.tooltip.body`),
                    },
                  };
                }),
              },
            ],
          },
          {
            name: 'scenario_selector_nrp_mode_options',
            type: WidgetType.a_stack,
            visible: true,
            relatedData: {
              className: 'gap-4',
            },
            widgets: [
              {
                name: 'scenario_selector_nrp_mode_label',
                type: WidgetType.a_new_tab_link,
                visible: true,
                relatedData: {
                  label: t('shared.scenarioAnalysis.scenarioSelector.form.fields.nrp_mode.label'),
                  url: NRP_METHODOLOGY_URL,
                  className: HEADER_CLASSNAME,
                },
              },
              {
                name: 'scenario_selector_nrp_mode_radio_group',
                type: WidgetType.o_form_radio_group,
                visible: true,
                relatedData: {
                  name: 'nrp_mode',
                  className: 'gap-3',
                },
                widgets: Object.keys(NRPMode).map((nrpMode) => {
                  return {
                    name: `scenario_selector_nrp_mode_option_${nrpMode}`,
                    type: WidgetType.o_form_radio_item,
                    visible: true,
                    relatedData: {
                      styles: {
                        label: 'whitespace-break-spaces',
                      },
                      key: `nrp-mode-radio-${nrpMode}`,
                      label: (
                        <span className=''>
                          {t(
                            `shared.scenarioAnalysis.scenarioSelector.form.fields.nrp_mode.options.${nrpMode as keyof typeof NRPMode}`,
                          )}
                        </span>
                      ),
                      value: `${nrpMode}`,
                    },
                  };
                }),
              },
            ],
          },
          {
            name: 'scenario_selector_actions',
            type: WidgetType.a_stack,
            visible: false,
            relatedData: {
              className: 'flex flex-row justify-between py-1 gap-4',
            },
            widgets: [
              {
                name: 'compare_filters_cancel_button',
                type: WidgetType.o_form_clear_button,
                visible: true,
                relatedData: {
                  text: t('global.ui.buttons.cancel'),
                  className: 'w-fit',
                  variant: 'outline',
                },
              },
              {
                name: 'scenario_selector_submit',
                type: WidgetType.o_form_submit_button,
                visible: true,
                relatedData: {
                  text: t('global.ui.buttons.apply'),
                  className: 'w-fit',
                },
              },
            ],
          },
        ],
      },
    ],
  };

  return <WidgetSlotRenderer nestedWidget={defaultConfiguration} />;
};
